import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { useAddNewCountryMutation } from "./countriesApiSlice"
import useLang from '../../hooks/useLang'
import useTranslate from '../../hooks/useTranslate'

// import `ChakraProvider` component
import { Box, Grid, GridItem, Center, HStack, Container, Input, Select, Badge, Icon, Image, Button, IconButton, Text, useDisclosure, useToast } from '@chakra-ui/react'
import { FormControl, FormLabel, FormErrorMessage, FormHelperText } from "@chakra-ui/react"

// import `ChakraProvider` icon
import { ArrowBackIcon, CheckIcon, AddIcon, MinusIcon } from '@chakra-ui/icons'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faEarthAmericas } from "@fortawesome/free-solid-svg-icons"

const NewCountryForm = ({ markets }) => {

    const { lang } = useLang()
    const { T } = useTranslate(lang)

    const [addNewCountry, {
        isLoading,
        isSuccess,
        isError,
        error
    }] = useAddNewCountryMutation()

    const navigate = useNavigate()

    let [canSave, setCanSave] = useState(false)

    const [countryIso, setCountryIso] = useState('')
    const [marketId, setMarketId] = useState(markets[0].id)

    const [formFieldsCountryName, setFormFields] = useState([
        { lang: 'en', countryName: '' }, { lang: 'fr', countryName: '' },
    ])

    const toastInfo = useToast({
        position: 'bottom-left',
        variant: 'left-accent',
    })

    const onPageBack = () => navigate('/dash/countries')

    const onCountryIsoChanged = e => setCountryIso(e.target.value)
    const onMarketIdChanged = e => setMarketId(e.target.value)

    const checkCanSave = () => {
        {
            setCanSave(true)
            if ((countryIso === '')) {
                setCanSave(false)
            }
            formFieldsCountryName.map((form) => {
                if (!isLoading && (form.lang === '' || form.countryName === '')) {
                    setCanSave(false)
                }
            })
        }
    }

    const onSaveCountryClicked = async (e) => {
        e.preventDefault()
        if (canSave) {
            let output = []
            let countryNameByLang = {}
            formFieldsCountryName.forEach(field => {
                countryNameByLang[field.lang] = field.countryName
            })
            output['countryName'] = countryNameByLang
            console.log(output)

            await addNewCountry({ marketId: marketId, countryName: countryNameByLang, countryIso })
        }

    }

    const options = markets.map(market => {
        return (
            <option
                key={market.id}
                value={market.id}
            > {market.marketName[lang]}</option >
        )
    })

    const handleFormChange = (event, index) => {
        let data = [...formFieldsCountryName];
        data[index][event.target.name] = event.target.value;
        setFormFields(data);
        checkCanSave()
    }

    const addFields = () => {
        let object = {
            lang: '',
            countryName: ''
        }
        setFormFields([...formFieldsCountryName, object])
        setCanSave(false)
    }

    const removeFields = (index) => {
        let data = [...formFieldsCountryName];
        data.splice(index, 1)
        setFormFields(data)
    }

    useEffect(() => {
        checkCanSave()
        if (isSuccess) {
            toastInfo({ title: 'COUNTRY : Add successfully', status: 'success' })
            setCountryIso('')
            setMarketId('')
            navigate('/dash/countries')
        }
        if (isError) {
            toastInfo({ title: 'COUNTRY : Add error (' + error?.data?.message + ')', status: 'error' })
        }
    }, [checkCanSave, isError, error, isSuccess, navigate])

    const content = (
        <>
            <Center bg='#45413C' p='0' color='white' axis='both'>
                <Grid w='95vw' h='100vh' maxW='1500' gap={5} p='5' templateColumns='repeat(12, 1fr)'>
                    <GridItem color='black' colSpan={{ base: "12", sm: "12", md: "12", lg: "12", xl: "12" }}>
                        <HStack color='white'>
                            <Container color='262626' textAlign={['left']} >
                                <Text fontSize={{ base: "24", sm: "24", md: "24", lg: "24", xl: "28" }} fontWeight={600}><FontAwesomeIcon icon={faEarthAmericas} /> {T.countries.NewCountry}</Text><br />
                            </Container>
                            <Container color='262626' textAlign={['right']}>
                                <Button size='sm' onClick={onPageBack} leftIcon={<ArrowBackIcon />} colorScheme='gray' variant='solid' mr='2'>
                                    <Text display={{ base: 'none', sm: 'flex' }} fontSize={{ base: "18", sm: "18", md: "18", lg: "18", xl: "20" }} fontWeight={600}>{T.common.Back}</Text>
                                </Button>
                                <Button
                                    size='sm'
                                    onClick={onSaveCountryClicked}
                                    disabled={!canSave}
                                    leftIcon={<CheckIcon />}
                                    colorScheme={canSave ? "teal" : "gray"}
                                >
                                    <Text display={{ base: 'none', sm: 'flex' }} fontSize={{ base: "18", sm: "18", md: "18", lg: "18", xl: "20" }} fontWeight={600}>{T.common.Save}</Text>
                                </Button>
                            </Container>
                        </HStack>
                        <Box borderRadius='xl' p={2} fontSize={20} fontWeight={400} color='black' border='10px' borderColor='black' bg='white' w="100%">

                            <form id="form" name="form" onSubmit={onSaveCountryClicked}>
                                <FormControl>

                                    <FormLabel htmlFor='country-status' mt='5' mb='0'>
                                        {T.countries.CountryName}
                                    </FormLabel>
                                    {formFieldsCountryName.map((form, index) => {
                                        return (
                                            <HStack key={index}>
                                                <Input
                                                    isInvalid={!form.lang}
                                                    name="lang"
                                                    placeholder={T.countries.IsoCode}
                                                    onChange={event => handleFormChange(event, index)}
                                                    value={form.lang}
                                                    focusBorderColor='blue.400'
                                                    errorBorderColor='red.300'
                                                    minLength="2"
                                                    maxLength="2"
                                                    bg='yellow.100'
                                                    w='10vw'
                                                />
                                                <Input
                                                    isInvalid={!form.countryName}
                                                    name='countryName'
                                                    placeholder={T.countries.TranslatedName}
                                                    onChange={event => handleFormChange(event, index)}
                                                    value={form.countryName}
                                                    focusBorderColor='blue.400'
                                                    errorBorderColor='red.300'
                                                    bg='yellow.100'
                                                    w='50vw'
                                                />
                                                {index !== 0 &&
                                                    <IconButton
                                                        size='xs'
                                                        name="Remove"
                                                        title={T.common.Delete}
                                                        colorScheme='gray'
                                                        aria-label={T.common.Delete}
                                                        icon={<MinusIcon />}
                                                        onClick={() => removeFields(index)}
                                                    />
                                                }

                                            </HStack>
                                        )
                                    })}
                                    <IconButton
                                        size='xs'
                                        title={T.common.Add}
                                        onClick={addFields}
                                        colorScheme='gray'
                                        aria-label={T.common.Add}
                                        icon={<AddIcon />}
                                    />

                                    <FormLabel htmlFor='country-status' mt='5' mb='0'>
                                        {T.countries.CountryISO}
                                    </FormLabel>
                                    <Input
                                        isInvalid={!countryIso}
                                        id="countryIso"
                                        name="countryIso"
                                        placeholder={T.countries.IsoCode}
                                        onChange={onCountryIsoChanged}
                                        value={countryIso}
                                        focusBorderColor='blue.400'
                                        errorBorderColor='red.300'
                                        bg='yellow.100'
                                    />

                                    <FormLabel htmlFor='country-status' mt='5' mb='0'>
                                        {T.countries.MarketArea}
                                    </FormLabel>
                                    <Select
                                        id="marketId"
                                        name="marketId"
                                        value={marketId}
                                        onChange={onMarketIdChanged}
                                        bg='yellow.100'
                                    >
                                        {options}
                                    </Select>

                                </FormControl>
                            </form>

                        </Box>
                    </GridItem>
                </Grid>
            </Center>

        </>
    )

    return content
}

export default NewCountryForm